import React, { useEffect, useMemo, useState } from 'react';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {
  Alert,
  Box,
  Card,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

function MetricCard({ label, value, unit = '', trend = null, accent = '#17323c' }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.8,
        borderRadius: 4,
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(18,44,52,0.08)',
      }}
    >
      <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="flex-end" spacing={0.8}>
        <Typography variant="h5" sx={{ color: accent, fontWeight: 900 }}>
          {value}
        </Typography>
        {unit && <Typography variant="caption" sx={{ color: '#8b9299', mb: 0.25 }}>{unit}</Typography>}
        {trend !== null && (
          <Box
            sx={{
              ml: 'auto',
              px: 1,
              py: 0.4,
              borderRadius: 2,
              bgcolor: trend >= 0 ? 'rgba(31,122,77,0.12)' : 'rgba(164,62,42,0.12)',
              color: trend >= 0 ? '#1f7a4d' : '#a43e2a',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {trend >= 0 ? '+' : ''}{trend}%
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function PoolStatusCard({ metrics }) {
  const statusColor = metrics.status === 'ACTIVE'
    ? '#1f7a4d'
    : metrics.status === 'ERROR'
      ? '#a43e2a'
      : '#8b9299';

  const statusBg = metrics.status === 'ACTIVE'
    ? 'rgba(31,122,77,0.12)'
    : metrics.status === 'ERROR'
      ? 'rgba(164,62,42,0.12)'
      : 'rgba(139,146,153,0.12)';

  const statusIcon = metrics.status === 'ACTIVE'
    ? <CheckCircleRoundedIcon sx={{ color: statusColor }} />
    : metrics.status === 'ERROR'
      ? <ErrorOutlineRoundedIcon sx={{ color: statusColor }} />
      : <AnalyticsRoundedIcon sx={{ color: statusColor }} />;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        background: statusBg,
        border: `1px solid ${statusColor}33`,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ mt: 0.3 }}>
          {statusIcon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ color: statusColor, fontWeight: 700, mb: 0.4 }}>
            État du pool
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', mb: 1 }}>
            {metrics.status === 'ACTIVE'
              ? 'Le pool de threads est actif et traite les générations d\'images.'
              : metrics.status === 'ERROR'
                ? 'Erreur lors du chargement des métriques du pool.'
                : 'Pool en attente d\'initialisation.'}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label={`Threads: ${metrics.poolSize}/${metrics.maxThreads}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.4)', fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`File: ${metrics.queueSize}/${metrics.queueCapacity}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.4)', fontWeight: 600 }}
            />
            {metrics.error && (
              <Chip
                size="small"
                label={metrics.error}
                sx={{ bgcolor: '#fde2e4', color: '#a43e2a', fontWeight: 600 }}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function UtilizationChart({ activePercent, queuePercent }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(18,44,52,0.08)' }}>
      <Stack spacing={2}>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
            <Typography variant="body2" sx={{ color: '#4f646f', fontWeight: 600 }}>
              Utilisation des threads actifs
            </Typography>
            <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 800 }}>
              {activePercent}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={activePercent}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: 'rgba(18,44,52,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: activePercent > 80 ? '#d45f43' : activePercent > 50 ? '#d89b1f' : '#1f7a4d',
              },
            }}
          />
        </Box>

        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
            <Typography variant="body2" sx={{ color: '#4f646f', fontWeight: 600 }}>
              Saturation de la file d'attente
            </Typography>
            <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 800 }}>
              {queuePercent}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={queuePercent}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: 'rgba(18,44,52,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: queuePercent > 80 ? '#d45f43' : queuePercent > 50 ? '#d89b1f' : '#8fd6b1',
              },
            }}
          />
        </Box>
      </Stack>
    </Paper>
  );
}

const PatternImageQueueMonitoringPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updateInterval, setUpdateInterval] = useState(2000); // 2 secondes par défaut

  // Fetch metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await fetch('/api/admin/job/pattern-image-queue-summary');
        if (!res.ok) throw new Error('Impossible de charger les métriques');
        const data = await res.json();
        setMetrics(data);
        setError(null);
        setLastUpdate(new Date());
      } catch (err) {
        setError(err.message);
        setMetrics(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, updateInterval);
    return () => clearInterval(interval);
  }, [updateInterval]);

  // Format timestamp
  const formatTime = (date) => {
    if (!date) return 'n/a';
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  };

  // Calculate average time
  const avgTimeDisplay = useMemo(() => {
    if (!metrics?.avgTimePerTaskMs) return 'n/a';
    const ms = metrics.avgTimePerTaskMs;
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }, [metrics?.avgTimePerTaskMs]);

  // Default metrics object for consistency
  const displayMetrics = metrics || {
    status: 'LOADING',
    activeThreads: 0,
    maxThreads: 0,
    poolSize: 0,
    queueSize: 0,
    queueCapacity: 0,
    totalPending: 0,
    totalCompleted: 0,
    activePercent: 0,
    queuePercent: 0,
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        {/* Header */}
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                icon={<GetAppRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
                label="Monitoring temps réel"
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                File de génération d'images patterns
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Vue en temps réel du pool de threads dédié à la génération asynchrone des images de patterns.
                Les tâches de génération s'exécutent en parallèle lors de la synchronisation du marché.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <Chip
                label={`Actualisation: ${updateInterval / 1000}s`}
                sx={{ bgcolor: 'rgba(247,243,234,0.12)', color: '#f7f3ea', fontWeight: 700 }}
              />
              <Chip
                label={`Dernier scan: ${formatTime(lastUpdate)}`}
                sx={{ bgcolor: 'rgba(247,243,234,0.12)', color: '#f7f3ea', fontWeight: 700 }}
              />
              <Chip
                label={`État: ${displayMetrics.status}`}
                sx={{
                  bgcolor: displayMetrics.status === 'ACTIVE'
                    ? 'rgba(143,214,177,0.24)'
                    : displayMetrics.status === 'ERROR'
                      ? 'rgba(212,95,67,0.24)'
                      : 'rgba(139,146,153,0.24)',
                  color: '#f7f3ea',
                  fontWeight: 700,
                }}
              />
            </Stack>
          </Stack>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ borderRadius: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Erreur: {error}
            </Typography>
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Alert severity="info" sx={{ borderRadius: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Chargement des métriques du pool de threads...
            </Typography>
          </Alert>
        )}

        {/* Main Content */}
        {!loading && !error && (
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
            {/* Left Column - Status and Pool Info */}
            <Paper sx={{ flex: 1, p: 3, borderRadius: 5, background: '#fffaf2' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 2 }}>
                État du service
              </Typography>

              <Stack spacing={2.5}>
                {/* Pool Status */}
                <PoolStatusCard metrics={displayMetrics} />

                {/* Core Metrics */}
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, px: 0.5 }}>
                    Configuration du pool
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <MetricCard
                      label="Threads actifs"
                      value={displayMetrics.activeThreads}
                      unit={`/${displayMetrics.maxThreads}`}
                      accent="#d89b1f"
                    />
                    <MetricCard
                      label="Threads créés"
                      value={displayMetrics.poolSize}
                      unit={`core: ${displayMetrics.coreThreads || 0}`}
                      accent="#1f7a4d"
                    />
                    <MetricCard
                      label="File d'attente"
                      value={displayMetrics.queueSize}
                      unit={`/${displayMetrics.queueCapacity}`}
                      accent={displayMetrics.queuePercent > 80 ? '#d45f43' : '#17323c'}
                    />
                    <MetricCard
                      label="Tâches en attente"
                      value={displayMetrics.totalPending}
                      accent="#8b9299"
                    />
                  </Box>
                </Stack>

                {/* Throughput Metrics */}
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, px: 0.5 }}>
                    Débit
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
                    <MetricCard
                      label="Complétées"
                      value={displayMetrics.totalCompleted}
                      accent="#1f7a4d"
                    />
                    <MetricCard
                      label="Temps moyen/image"
                      value={avgTimeDisplay}
                      accent="#17323c"
                    />
                    <MetricCard
                      label="Soumises au total"
                      value={displayMetrics.totalSubmitted}
                      accent="#8b9299"
                    />
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* Right Column - Utilization Charts */}
            <Paper sx={{ flex: 0.85, p: 3, borderRadius: 5, background: '#fffaf2' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 2 }}>
                Utilisation du pool
              </Typography>

              <Stack spacing={3}>
                <UtilizationChart
                  activePercent={displayMetrics.activePercent}
                  queuePercent={displayMetrics.queuePercent}
                />

                {/* Details */}
                <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: 'rgba(31,122,77,0.08)', border: '1px solid rgba(31,122,77,0.16)' }}>
                  <Typography variant="subtitle2" sx={{ color: '#1f7a4d', fontWeight: 700, mb: 0.8 }}>
                    Conseil d'exploitation
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                    {displayMetrics.activePercent > 90
                      ? 'Le pool fonctionne à pleine capacité. Les futures tâches pourront être en attente.'
                      : displayMetrics.queuePercent > 70
                        ? 'La file d\'attente se remplit rapidement. Considérez une augmentation du maxPoolSize.'
                        : 'Les ressources du pool sont utilisées de manière équilibrée.'}
                  </Typography>
                </Paper>

                {/* Timestamp */}
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ color: '#6c7a80', fontWeight: 600 }}>
                      Dernière actualisation
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700 }}>
                      {formatTime(lastUpdate)}
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Stack>
        )}

        {/* Footer Info */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, background: 'rgba(35,84,110,0.08)', border: '1px solid rgba(35,84,110,0.16)' }}>
          <Typography variant="caption" sx={{ color: '#4f646f', display: 'block', mb: 0.6, fontWeight: 600 }}>
            À propos du monitoring
          </Typography>
          <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
            Ce tableau de bord suit en temps réel l'état du pool de threads <code>patternImageGenerationExecutor</code>.
            Les images de patterns sont générées de manière asynchrone après chaque synchronisation de données.
            Si la file d'attente se remplit rapidement, cela signifie que la génération d'images prend plus de temps que l'import des cryptos.
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
};

export default PatternImageQueueMonitoringPage;
